import moment from 'moment';
import { TokenIcons } from '../../../../assets/icons';
import Button from '../../../../components/Button';
import Card from '../../../../components/Card';
import {
    OrderBookActionMapper,
    OrderBookType,
    Tokens,
} from '../../../../config/constants';
import useModalState from '../../../../context/modal/useModalState';
import { formatAmountWithSuffix, formatUSD } from '../../../../helpers';
import { UserOrder } from '../../../../hooks/web3/trade/useUserOpenOrders';
import AssetType from '../../../../components/AssetType';
import InfoPopUp from '../../../../components/InfoPopUp';
import clsx from 'clsx';
import { EditPositionType } from '../../../../modals/trade/EditPositionModal/EditPosition';
import { ClosePositionType } from '../../../../modals/trade/ClosePositionModal/ClosePosition';
import useCancelOrder from '../../../../hooks/web3/trade/useCancelOrder';
import MarkPrice from '../MarkPrice';

type OrderRowProps = {
    order: UserOrder;
};

const OrderBookPositionTypeMapper: Record<
    Extract<
        OrderBookType,
        | OrderBookType.ADD_MARGIN
        | OrderBookType.REMOVE_MARGIN
        | OrderBookType.EXTEND
        | OrderBookType.EXTEND_LEVERAGE
        | OrderBookType.CLOSE
        | OrderBookType.PARTIALLY_CLOSE
    >,
    EditPositionType | ClosePositionType
> = {
    [OrderBookType.EXTEND_LEVERAGE]: EditPositionType.ADJUST,
    [OrderBookType.ADD_MARGIN]: EditPositionType.DEPOSIT,
    [OrderBookType.REMOVE_MARGIN]: EditPositionType.WITHDRAW,
    [OrderBookType.EXTEND]: EditPositionType.SIZE,
    [OrderBookType.CLOSE]: ClosePositionType.LIMIT,
    [OrderBookType.PARTIALLY_CLOSE]: ClosePositionType.LIMIT,
};

const isPositionType = (
    orderBookType: OrderBookType
): orderBookType is keyof typeof OrderBookPositionTypeMapper => {
    return Object.keys(OrderBookPositionTypeMapper).includes(orderBookType);
};

const OrderRow: React.FC<OrderRowProps> = ({ order }) => {
    const { openModal, setModalData } = useModalState();
    const cancel = useCancelOrder();

    const handleEdit = () => {
        setModalData((prev) => ({
            ...prev,
            data: {
                position: order,
                defaultTab: [OrderBookType.TAKE_PROFIT, OrderBookType.STOP_LOSS].includes(
                    order.orderType
                )
                    ? 1 // TP/SL tab
                    : 0, // Edit Position Tab
                defaultEditTab: isPositionType(order.orderType)
                    ? OrderBookPositionTypeMapper[order.orderType]
                    : undefined,
            },
        }));

        openModal(
            [OrderBookType.CLOSE, OrderBookType.PARTIALLY_CLOSE].includes(order.orderType)
                ? 'closePositionModal'
                : 'editPositionModal'
        );
    };

    return (
        <div className='row'>
            <Card>
                <div className='orders__mark-price'>
                    {TokenIcons[order.tradePair?.asset.symbol ?? Tokens.FALLBACK]}
                    <span className='column-label'>
                        {order.tradePair?.asset.symbol ?? Tokens.FALLBACK} Price
                    </span>
                    <MarkPrice pool={order.tradePair} />
                </div>
                <div className={`orders__type ${order.isShort ? 'short' : 'long'}`}>
                    <AssetType
                        tradePair={order.tradePair}
                        isShort={order.isShort}
                        leverage={order.leverage}
                    />
                </div>
                <div className='orders__action'>
                    <span className='column-label'>Action</span>
                    {OrderBookActionMapper[order.orderType]}
                </div>
                <div className='orders__size'>
                    <span className='column-label'>Size</span>
                    <span>
                        {formatAmountWithSuffix(order.volume || 0, {
                            minDecimals: 2,
                            currency: 'USD',
                            minValue: 1_000_000_000,
                        })}
                    </span>
                </div>
                <div className='orders__collateral'>
                    <span className='column-label'>Collateral</span>
                    <InfoPopUp
                        trigger={
                            <span className='value'>
                                {formatAmountWithSuffix(order.margin || 0, {
                                    minDecimals: 2,
                                    currency: 'USD',
                                    minValue: 1_000_000_000,
                                })}
                            </span>
                        }
                    >
                        Initial Collateral:{' '}
                        {formatAmountWithSuffix(order.entryMargin || 0, {
                            minDecimals: 2,
                            currency: 'USD',
                            minValue: 1_000_000_000,
                        })}
                    </InfoPopUp>
                </div>
                <div className='orders__limit-price'>
                    <span className='column-label'>Limit Price</span>
                    {formatUSD(order.limitPrice || 0, {
                        minDecimals: 2,
                        maxDecimals: order.tradePair.decimalFormatting,
                    })}
                </div>
                <div className='orders__liquidation-price'>
                    <span className='column-label'>Liq. Price</span>
                    {formatUSD(order.bankruptcyPrice || 0, {
                        minDecimals: 2,
                        maxDecimals: order.tradePair.decimalFormatting,
                    })}
                </div>
                <div className='orders__deadline'>
                    <span className='column-label'>Deadline</span>
                    {moment.unix(order.deadline).format('Do MMM YY,')} <br />
                    {moment.unix(order.deadline).format('HH:mm:ss')}
                </div>
            </Card>
            <div
                className={clsx('orders__action-container', {
                    single: order.orderType === OrderBookType.OPEN,
                })}
            >
                {order.orderType !== OrderBookType.OPEN && (
                    <Button onClick={handleEdit}>
                        <span>Edit</span>
                        <span>&nbsp;Order</span>
                    </Button>
                )}
                <Button onClick={() => cancel(order.orderId, order.orderType)}>
                    <span>Cancel</span>
                    <span>&nbsp;Order</span>
                </Button>
            </div>
        </div>
    );
};

export default OrderRow;
